import type { DatabaseEngineId } from '@/core/engines';
import type { SqlSnippetTemplate } from './sqlSnippetTemplateTypes';
import { byEngine } from './sqlSnippetTemplateUtils';

export function getBuiltInSqlSnippetTemplatesDmlWrite(engineId: DatabaseEngineId): readonly SqlSnippetTemplate[] {
  const templates: SqlSnippetTemplate[] = [
    {
      id: 'insert-values',
      label: 'INSERT INTO … VALUES …',
      detail: 'Built-in snippet',
      documentation: 'Basic INSERT template',
      filterText: 'insert into values',
      insertText: 'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values});',
    },
    {
      id: 'insert-multi-row',
      label: byEngine(engineId, { mssql: 'INSERT INTO … VALUES … (multi-row)', pgsql: 'INSERT INTO … VALUES … (multi-row)', oracle: 'INSERT ALL … (multi-row)' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, { mssql: 'Multi-row INSERT template', pgsql: 'Multi-row INSERT template', oracle: 'Multi-table INSERT ALL template (Oracle)' }),
      filterText: byEngine(engineId, { mssql: 'insert into values multi row', pgsql: 'insert into values multi row', oracle: 'insert all into multi row' }),
      insertText: byEngine(engineId, {
        mssql: 'INSERT INTO ${1:table} (${2:columns})\nVALUES\n  (${3:values1}),\n  (${4:values2});',
        pgsql: 'INSERT INTO ${1:table} (${2:columns})\nVALUES\n  (${3:values1}),\n  (${4:values2});',
        oracle: 'INSERT ALL\n  INTO ${1:table} (${2:columns}) VALUES (${3:values1})\n  INTO ${1:table} (${2:columns}) VALUES (${4:values2})\nSELECT 1 FROM DUAL;',
      }),
    },
    {
      id: 'insert-returning',
      label: byEngine(engineId, { mssql: 'INSERT … OUTPUT …', pgsql: 'INSERT … RETURNING …', sqlite: 'INSERT … RETURNING …', oracle: 'INSERT … RETURNING … INTO …' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, {
        mssql: 'INSERT with OUTPUT inserted.* (SQL Server)',
        pgsql: 'INSERT with RETURNING * (PostgreSQL)',
        sqlite: 'INSERT with RETURNING * (SQLite)',
        oracle: 'INSERT with RETURNING INTO (Oracle)',
      }),
      filterText: byEngine(engineId, { mssql: 'insert output inserted', pgsql: 'insert returning', sqlite: 'insert returning', oracle: 'insert returning into' }),
      insertText: byEngine(engineId, {
        mssql: 'INSERT INTO ${1:table} (${2:columns})\nOUTPUT inserted.*\nVALUES (${3:values});',
        pgsql: 'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values})\nRETURNING *;',
        sqlite: 'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values})\nRETURNING *;',
        oracle: 'DECLARE\n  v_id NUMBER;\nBEGIN\n  INSERT INTO ${1:table} (${2:columns})\n  VALUES (${3:values})\n  RETURNING ${4:id} INTO v_id;\n  DBMS_OUTPUT.PUT_LINE(\'Inserted ID: \' || v_id);\nEND;\n/',
      }),
    },
    {
      id: 'insert-select',
      label: 'INSERT INTO … SELECT …',
      detail: 'Built-in snippet',
      documentation: 'INSERT from SELECT template',
      filterText: 'insert into select',
      insertText:
        'INSERT INTO ${1:target_table} (${2:columns})\nSELECT\n  ${3:columns}\nFROM ${4:source_table}\nWHERE ${5:condition};',
    },
    {
      id: 'upsert',
      label: byEngine(engineId, { mssql: 'UPSERT (MERGE …)', pgsql: 'UPSERT (INSERT … ON CONFLICT …)', sqlite: 'UPSERT (INSERT … ON CONFLICT …)', mysql: 'UPSERT (INSERT … ON DUPLICATE KEY UPDATE …)', oracle: 'UPSERT (MERGE …)' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, { mssql: 'UPSERT template using MERGE (SQL Server)', pgsql: 'UPSERT template using ON CONFLICT (PostgreSQL)', sqlite: 'UPSERT template using ON CONFLICT (SQLite)', mysql: 'UPSERT template using ON DUPLICATE KEY UPDATE (MySQL)', oracle: 'UPSERT template using MERGE (Oracle)' }),
      filterText: byEngine(engineId, { mssql: 'upsert insert on conflict merge', pgsql: 'upsert insert on conflict', mysql: 'upsert insert on duplicate key update', oracle: 'upsert merge into matched' }),
      insertText: byEngine(engineId, {
        mssql:
          'MERGE INTO ${1:target} AS t\nUSING (${2:source_query}) AS s\n  ON ${3:t.id} = ${4:s.id}\nWHEN MATCHED THEN\n  UPDATE SET ${5:t.col} = ${6:s.col}\nWHEN NOT MATCHED THEN\n  INSERT (${7:columns}) VALUES (${8:values});',
        pgsql:
          'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values})\nON CONFLICT (${4:conflict_columns})\nDO UPDATE SET ${5:column} = ${6:excluded.column};',
        sqlite:
          'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values})\nON CONFLICT (${4:conflict_columns})\nDO UPDATE SET ${5:column} = ${6:excluded.column};',
        mysql:
          'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values})\nON DUPLICATE KEY UPDATE ${4:column} = VALUES(${4:column});',
        oracle:
          'MERGE INTO ${1:target} t\nUSING (${2:SELECT 1 FROM DUAL}) s\n  ON (${3:t.id} = ${4:s.id})\nWHEN MATCHED THEN\n  UPDATE SET ${5:t.col} = ${6:s.col}\nWHEN NOT MATCHED THEN\n  INSERT (${7:columns}) VALUES (${8:values});',
      }),
    },
    {
      id: 'update-where',
      label: 'UPDATE … SET … WHERE …',
      detail: 'Built-in snippet',
      documentation: 'UPDATE with WHERE template',
      filterText: 'update set where',
      insertText: 'UPDATE ${1:table}\nSET ${2:column} = ${3:value}\nWHERE ${4:condition};',
    },
    {
      id: 'update-from',
      label: byEngine(engineId, { mssql: 'UPDATE … FROM … JOIN …', pgsql: 'UPDATE … SET … FROM …', sqlite: 'UPDATE … SET … (correlated)', oracle: 'UPDATE … SET … (correlated subquery)' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, {
        mssql: 'UPDATE with JOIN template (SQL Server)',
        pgsql: 'UPDATE with FROM template (PostgreSQL)',
        sqlite: 'UPDATE with correlated subquery (SQLite)',
        oracle: 'UPDATE with correlated subquery (Oracle)',
      }),
      filterText: 'update from join',
      insertText: byEngine(engineId, {
        mssql:
          'UPDATE t\nSET t.${1:column} = ${2:value}\nFROM ${3:table} t\nJOIN ${4:other_table} o ON ${5:t.id} = ${6:o.table_id}\nWHERE ${7:condition};',
        pgsql:
          'UPDATE ${1:table} t\nSET ${2:column} = ${3:value}\nFROM ${4:other_table} o\nWHERE ${5:t.id} = ${6:o.table_id}\n  AND ${7:condition};',
        sqlite:
          'UPDATE ${1:table}\nSET ${2:column} = (\n  SELECT ${3:o.value}\n  FROM ${4:other_table} o\n  WHERE ${5:o.table_id} = ${1:table}.${6:id}\n)\nWHERE ${7:condition};',
        oracle:
          'UPDATE ${1:table} t\nSET ${2:column} = (\n  SELECT ${3:o.value}\n  FROM ${4:other_table} o\n  WHERE ${5:o.table_id} = t.${6:id}\n)\nWHERE ${7:condition};',
      }),
    },
    {
      id: 'update-returning',
      label: byEngine(engineId, { mssql: 'UPDATE … OUTPUT …', pgsql: 'UPDATE … RETURNING …', sqlite: 'UPDATE … RETURNING …', oracle: 'UPDATE … RETURNING … INTO …' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, { mssql: 'UPDATE with OUTPUT inserted.* (SQL Server)', pgsql: 'UPDATE with RETURNING * (PostgreSQL)', sqlite: 'UPDATE with RETURNING * (SQLite)', oracle: 'UPDATE with RETURNING INTO (Oracle)' }),
      filterText: byEngine(engineId, { mssql: 'update output inserted', pgsql: 'update returning', sqlite: 'update returning', oracle: 'update returning into' }),
      insertText: byEngine(engineId, {
        mssql: 'UPDATE ${1:table}\nSET ${2:column} = ${3:value}\nOUTPUT inserted.*\nWHERE ${4:condition};',
        pgsql: 'UPDATE ${1:table}\nSET ${2:column} = ${3:value}\nWHERE ${4:condition}\nRETURNING *;',
        sqlite: 'UPDATE ${1:table}\nSET ${2:column} = ${3:value}\nWHERE ${4:condition}\nRETURNING *;',
        oracle: 'DECLARE\n  v_val ${3:VARCHAR2(100)};\nBEGIN\n  UPDATE ${1:table}\n  SET ${2:column} = ${3:value}\n  WHERE ${4:condition}\n  RETURNING ${2:column} INTO v_val;\n  DBMS_OUTPUT.PUT_LINE(\'Updated: \' || v_val);\nEND;\n/',
      }),
    },
    {
      id: 'delete-where',
      label: 'DELETE FROM … WHERE …',
      detail: 'Built-in snippet',
      documentation: 'DELETE with WHERE template',
      filterText: 'delete from where',
      insertText: 'DELETE FROM ${1:table}\nWHERE ${2:condition};',
    },
    {
      id: 'delete-using',
      label: byEngine(engineId, { mssql: 'DELETE … FROM … JOIN …', pgsql: 'DELETE … USING …', sqlite: 'DELETE … WHERE EXISTS …', oracle: 'DELETE … WHERE EXISTS …' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, {
        mssql: 'DELETE with JOIN template (SQL Server)',
        pgsql: 'DELETE with USING template (PostgreSQL)',
        sqlite: 'DELETE with correlated EXISTS (SQLite)',
        oracle: 'DELETE with correlated EXISTS (Oracle)',
      }),
      filterText: 'delete join using',
      insertText: byEngine(engineId, {
        mssql:
          'DELETE t\nFROM ${1:table} t\nJOIN ${2:other_table} o ON ${3:t.id} = ${4:o.table_id}\nWHERE ${5:condition};',
        pgsql:
          'DELETE FROM ${1:table} t\nUSING ${2:other_table} o\nWHERE ${3:t.id} = ${4:o.table_id}\n  AND ${5:condition};',
        sqlite:
          'DELETE FROM ${1:table}\nWHERE EXISTS (\n  SELECT 1\n  FROM ${2:other_table} o\n  WHERE ${3:o.table_id} = ${1:table}.${4:id}\n    AND ${5:condition}\n);',
        oracle:
          'DELETE FROM ${1:table} t\nWHERE EXISTS (\n  SELECT 1\n  FROM ${2:other_table} o\n  WHERE ${3:o.table_id} = t.${4:id}\n    AND ${5:condition}\n);',
      }),
    },
    {
      id: 'delete-returning',
      label: byEngine(engineId, { mssql: 'DELETE … OUTPUT …', pgsql: 'DELETE … RETURNING …', sqlite: 'DELETE … RETURNING …', oracle: 'DELETE … RETURNING … INTO …' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, { mssql: 'DELETE with OUTPUT deleted.* (SQL Server)', pgsql: 'DELETE with RETURNING * (PostgreSQL)', sqlite: 'DELETE with RETURNING * (SQLite)', oracle: 'DELETE with RETURNING INTO (Oracle)' }),
      filterText: byEngine(engineId, { mssql: 'delete output deleted', pgsql: 'delete returning', sqlite: 'delete returning', oracle: 'delete returning into' }),
      insertText: byEngine(engineId, {
        mssql: 'DELETE FROM ${1:table}\nOUTPUT deleted.*\nWHERE ${2:condition};',
        pgsql: 'DELETE FROM ${1:table}\nWHERE ${2:condition}\nRETURNING *;',
        sqlite: 'DELETE FROM ${1:table}\nWHERE ${2:condition}\nRETURNING *;',
        oracle: 'DECLARE\n  v_id NUMBER;\nBEGIN\n  DELETE FROM ${1:table}\n  WHERE ${2:condition}\n  RETURNING ${3:id} INTO v_id;\n  DBMS_OUTPUT.PUT_LINE(\'Deleted ID: \' || v_id);\nEND;\n/',
      }),
    },
  ];

  if (engineId === 'pgsql') {
    templates.push(
      {
        id: 'pg19-on-conflict-do-select',
        label: 'INSERT … ON CONFLICT DO SELECT … (PG19)',
        detail: 'Built-in snippet',
        documentation: 'Read-modify-insert without side effects — on conflict, return the existing row instead of updating it (PostgreSQL 19+).',
        filterText: 'insert on conflict do select returning pg19',
        insertText: 'INSERT INTO ${1:table} (${2:cols}) VALUES (${3:vals})\nON CONFLICT (${4:key}) DO SELECT\nRETURNING *;',
      },
      {
        id: 'pg19-update-for-portion-of',
        label: 'UPDATE … FOR PORTION OF … (PG19)',
        detail: 'Built-in snippet',
        documentation: 'Temporal UPDATE that splits a row at the period boundaries and updates only the given portion (PostgreSQL 19+). Requires a temporal primary key (PRIMARY KEY (…, period WITHOUT OVERLAPS)); btree_gist is needed for mixed-type keys.',
        filterText: 'update for portion of temporal without overlaps pg19',
        insertText: 'UPDATE ${1:table}\nFOR PORTION OF ${2:period} FROM ${3:\'start\'} TO ${4:\'end\'}\nSET ${5:col} = ${6:value}\nWHERE ${7:condition};',
      },
      {
        id: 'pg19-delete-for-portion-of',
        label: 'DELETE … FOR PORTION OF … (PG19)',
        detail: 'Built-in snippet',
        documentation: 'Temporal DELETE that removes only the given portion of a period, splitting the row at the boundaries (PostgreSQL 19+).',
        filterText: 'delete for portion of temporal pg19',
        insertText: 'DELETE FROM ${1:table}\nFOR PORTION OF ${2:period} FROM ${3:\'start\'} TO ${4:\'end\'}\nWHERE ${5:condition};',
      },
    );
  }

  return templates;
}
